import {
  CheckboxField,
  Col,
  ConfirmModal,
  Divider,
  Flex,
  Form,
  NumericFormatField,
  RadioGroupField,
  Row,
  Space,
  SubmitButton,
  Text,
  theme,
} from '@/components';
import { useForm } from '@/hooks';
import {
  FundSheetTypeLabelModifier,
  SheetTypeLabelModifier,
  fundSheetTypeEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo, useState } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useValidateSnapshotTarget } from '../_hooks/project-form-handler';
import { ProjectFormSchema } from '../_model/project-form';
import { KsProjectForm } from './ks/KsProjectForm';
import { RsProjectForm } from './rs/RsProjectForm';
import { RsSkippedReviewProjectForm } from './rs/RsSkippedReviewProjectForm';
import { SsProjectForm } from './ss/SsProjectForm';

type FormValues = z.infer<typeof ProjectFormSchema>;

type Props = {
  isSubmitEnabledOnMount?: boolean;
  initialValues?: {
    sheetType?: FormValues['sheetType'];
    data?: Partial<FormValues['data']>;
  };
  onSubmit: (values: FormValues) => Promise<void>;
  ignoredIdsForRelatedProject?: string[];
} & (
  | {
      type: 'edit';
      onCancelModal: () => void;
      onUpdateProject: (values: FormValues) => Promise<void>;
      openUpdateConfirmModal:
        | {
            isOpen: true;
            values: FormValues;
          }
        | {
            isOpen: false;
          };
    }
  | {
      type: 'new';
      openUpdateConfirmModal?: never;
      onCancelModal?: never;
      onUpdateProject?: never;
    }
);

export const ProjectForm: FC<Props> = ({
  type,
  isSubmitEnabledOnMount,
  ignoredIdsForRelatedProject,
  initialValues: _initialValues,
  onSubmit,
  openUpdateConfirmModal,
  onCancelModal,
  onUpdateProject,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = useMemo(() => {
    const data = _initialValues?.data ?? {};
    return {
      sheetType: _initialValues?.sheetType ?? sheetTypeEnum.Values.RS,
      data: {
        skipAdministrativeReformTeamEvaluation: false,
        ...data,
        // TODO: Parsed by sheetType
      } as any,
    };
  }, [_initialValues]);

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      ProjectFormSchema.superRefine((v, ctx) => {
        if (
          v.sheetType === 'RS' &&
          v.data.skipAdministrativeReformTeamEvaluation === false &&
          v.data.implementationMethods?.includes('subsidy')
        ) {
          v.data.subsidyRates?.forEach((rate, index) => {
            if (!rate.target || rate.target.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['data', 'subsidyRates', index, 'target'],
                message: t('validation.required'),
              });
            }
            if (!rate.ratesText || rate.ratesText.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['data', 'subsidyRates', index, 'ratesText'],
                message: t('validation.required'),
              });
            }
            if (!rate.limitText || rate.limitText.length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['data', 'subsidyRates', index, 'limitText'],
                message: t('validation.required'),
              });
            }
          });
        }

        const isKsSheet = v.sheetType === 'KS';
        const fundSheetType =
          isKsSheet && v.data.fundAdditionalAttributes?.fundSheetType;

        // 片方だけのバリデーションチェックを行うか確認する
        const isCheckOnlyOne = !isKsSheet || fundSheetType === 'investment';
        const isHasNotStart =
          isCheckOnlyOne && !v.data.period?.[0] && !v.data.isUnknownStart;
        const isHasNotEnd =
          isCheckOnlyOne && !v.data.period?.[1] && !v.data.isNoScheduledEnd;

        // 期間の少なくとも1つが設定されているか確認
        const hasAnyPeriod =
          !!v.data.period?.[0] ||
          !!v.data.period?.[1] ||
          v.data.isUnknownStart ||
          v.data.isNoScheduledEnd;

        if (
          fundSheetType !== 'local-government' &&
          (isHasNotStart || isHasNotEnd || !hasAnyPeriod)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: isHasNotStart
              ? t('project-form.validation.required-start')
              : isHasNotEnd
                ? t('project-form.validation.required-end')
                : t('validation.required'),
            path: ['data', 'period'],
          });
        }
      }),
    ),
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitSuccessful, isDirty },
    setValue,
    setError,
    watch,
    resetField,
  } = methods;

  const { fields: planFields, remove: removePlan } = useFieldArray({
    control,
    name: 'data.plans',
  });

  const [
    sheetType,
    fiscalYear,
    skipAdministrativeReformTeamEvaluation,
    fiscalYearOfSnapshot,
  ] = watch([
    'sheetType',
    'data.fiscalYear',
    'data.skipAdministrativeReformTeamEvaluation',
    'data.fiscalYearOfSnapshot',
  ]);

  const { validSnapshotTarget } = useValidateSnapshotTarget({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: fiscalYearOfSnapshot?.at(-1),
  });

  const shouldShowLeaveAlert = useMemo<boolean>(
    () =>
      // 一度submitした際に、バリデーションエラーが発生した場合に離脱ポップアップを表示させるため
      (isDirty &&
        !isSubmitting &&
        (!openUpdateConfirmModal?.isOpen || !isSubmitSuccessful)) ||
      !!openUpdateConfirmModal?.isOpen,
    [isDirty, isSubmitting, openUpdateConfirmModal, isSubmitSuccessful],
  );

  return (
    <>
      <FormProvider {...methods}>
        <Form
          leavePageAlertCondition={{
            additionalAlertCondition: shouldShowLeaveAlert,
          }}
          layout="vertical"
          onFinish={handleSubmit(async (values) => {
            const result = validSnapshotTarget({ setError, values });

            if (result.result === 'error') {
              setIsSubmitting(false);
              return;
            }
            try {
              setIsSubmitting(true);

              await onSubmit(values);
            } catch {
              setIsSubmitting(false);
            }
          })}
        >
          <Flex
            vertical
            style={{
              width: '100%',
              padding: '8px 16px',
              backgroundColor: token.colorBgLayout,
              marginBottom: 32,
            }}
          >
            <Space split={<Divider type="vertical" />}>
              <Space size="middle">
                <Text
                  text={t('sheet-type')}
                  color="secondary"
                  variant="footnote"
                />

                {type === 'edit' ? (
                  <Flex>
                    <Text text={`${SheetTypeLabelModifier[sheetType]}`} />
                    {sheetType === 'RS' && (
                      <Text
                        text={`（${
                          skipAdministrativeReformTeamEvaluation
                            ? t('skip-administrative-reform-team-evaluation')
                            : t('need-administrative-reform-team-evaluation')
                        }）`}
                      />
                    )}
                  </Flex>
                ) : (
                  <RadioGroupField
                    name="sheetType"
                    control={control}
                    errors={errors}
                    options={sheetTypeEnum.options.map((value) => ({
                      value,
                      label: SheetTypeLabelModifier[value],
                    }))}
                    shouldCoexistOnChange
                    onChange={({ target }) => {
                      const value = target.value as z.infer<
                        typeof sheetTypeEnum
                      >;
                      if (value === 'KS') {
                        setValue(
                          'data.fundAdditionalAttributes.fundSheetType',
                          'standard',
                          { shouldDirty: true },
                        );
                      }
                    }}
                    marginBottom={0}
                  />
                )}
              </Space>

              {sheetType === 'RS' && type === 'new' && (
                <CheckboxField
                  name="data.skipAdministrativeReformTeamEvaluation"
                  text={t('skip-administrative-reform-team-evaluation')}
                  control={control}
                  errors={errors}
                  wrapperStyle={{ marginBottom: 0 }}
                />
              )}
            </Space>

            {sheetType === 'KS' && (
              <>
                <Divider />

                <Row gutter={[24, 0]}>
                  <Col span={12}>
                    <RadioGroupField
                      name="data.fundAdditionalAttributes.fundSheetType"
                      label={t('fund-sheet-type')}
                      control={control}
                      errors={errors}
                      options={fundSheetTypeEnum.options.map((value) => ({
                        value,
                        label: FundSheetTypeLabelModifier[value],
                      }))}
                      disabled={type === 'edit'}
                      required
                      marginBottom={0}
                      shouldCoexistOnChange
                      labelSize="small"
                      onChange={(event) => {
                        const parsedFundSheetType = fundSheetTypeEnum.parse(
                          event.target.value,
                        );

                        if (parsedFundSheetType === 'investment') {
                          resetField(
                            'data.fundAdditionalAttributes.businessForms',
                            {
                              keepDirty: false,
                              keepError: false,
                              keepTouched: false,
                              defaultValue: [],
                            },
                          );
                          resetField(
                            'data.fundAdditionalAttributes.fundBusinessName',
                            {
                              keepDirty: false,
                              keepError: false,
                              keepTouched: false,
                              defaultValue: null,
                            },
                          );
                        }
                        if (parsedFundSheetType === 'local-government') {
                          setValue('data.legalBases', undefined, {
                            shouldDirty: true,
                          });
                          planFields.forEach((_, idx) => {
                            removePlan(idx);
                          });
                          setValue('data.plans', undefined, {
                            shouldDirty: true,
                          });
                        }
                      }}
                    />
                  </Col>
                  <Col span={6}>
                    <NumericFormatField
                      key="data.fundAdditionalAttributes.fundSheetNumber"
                      name="data.fundAdditionalAttributes.fundSheetNumber"
                      label={t('fund-sheet-number')}
                      control={control}
                      errors={errors}
                      min={1}
                      maxLength={4}
                      required
                      disableSeparator
                      marginBottom={0}
                      labelSize="small"
                    />
                  </Col>
                  <Col span={6}>
                    <NumericFormatField
                      key="data.fundAdditionalAttributes.fundSheetBranchNumber"
                      name="data.fundAdditionalAttributes.fundSheetBranchNumber"
                      label={t('fund-sheet-branch-number')}
                      control={control}
                      errors={errors}
                      min={1}
                      maxLength={2}
                      disableSeparator
                      marginBottom={0}
                      labelSize="small"
                    />
                  </Col>
                </Row>
              </>
            )}
          </Flex>

          {sheetType === 'RS' ? (
            <>
              {skipAdministrativeReformTeamEvaluation ? (
                <RsSkippedReviewProjectForm operation={type} />
              ) : (
                <RsProjectForm
                  operation={type}
                  ignoredIdsForRelatedProject={ignoredIdsForRelatedProject}
                />
              )}
            </>
          ) : sheetType === 'SS' ? (
            <SsProjectForm
              operation={type}
              ignoredIdsForRelatedProject={ignoredIdsForRelatedProject}
            />
          ) : (
            <KsProjectForm
              operation={type}
              ignoredIdsForRelatedProject={ignoredIdsForRelatedProject}
            />
          )}

          <Space style={{ marginTop: 32 }}>
            <SubmitButton
              loading={isSubmitting}
              disabled={!isSubmitEnabledOnMount && !isDirty}
            />
          </Space>
        </Form>
      </FormProvider>
      {type === 'edit' && (
        <ConfirmModal
          destroyOnHidden
          okText={t('save')}
          open={openUpdateConfirmModal.isOpen}
          title={t('save-confirmation')}
          disclaimerText={t('change-not-exist-in-value-confirmation')}
          onOk={async () => {
            if (openUpdateConfirmModal.isOpen) {
              onCancelModal();
              await onUpdateProject(openUpdateConfirmModal.values);
            }
          }}
          onCancel={() => {
            onCancelModal();
            setIsSubmitting(false);
          }}
          skipCheck
        />
      )}
    </>
  );
};
